import TimerEvent from "../events/TimerEvent.ts";
import Timer from "./Timer.ts";
import DelayData from "../types/DelayData.ts";

export default class Delay {
    //--------------------------------------------------------------------------
    //
    //  Properties
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    public static delayCalls: DelayData[];

    //--------------------------------------------------------------------------
    //
    //  Methods
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private static checkForDuplicateIds(id: string): void {
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].id && this.delayCalls[i].id == id) {
                    console.log("!!! WARNING: Duplicate ID detected. Did you mean to set two delays with the same ID?");
                    return;
                }
            }
        }
    }

    private static handleComplete = (event: CustomEvent) => {
        let curCall: DelayData;
        const len: number = Delay.delayCalls.length;
        for (let i: number = 0; i < len; i++) {
            if (Delay.delayCalls[i].timer === event.detail.target) {
                curCall = Delay.delayCalls[i];
                Delay.delayCalls.splice(i, 1);
                break;
            }
        }
        curCall.timer.reset();
        curCall.timer.removeCustomEventListener(TimerEvent.TIMER_COMPLETE, Delay.handleComplete);
        curCall.timer = null;
        if (curCall.params) {
            curCall.callback.apply(null, curCall.params);
        } else {
            curCall.callback();
        }
        curCall.callback = null;
        curCall.id = null;
        curCall.params = null;
        if (Delay.delayCalls && Delay.delayCalls.length == 0) {
            Delay.delayCalls = null;
        }
    };

    private static removeCall(id: string): void {
        if (this.delayCalls) {
            for (let i: number = 0; i < this.delayCalls.length; i++) {
                if (this.delayCalls[i].id == id) {
                    this.delayCalls[i].callback = null;
                    this.delayCalls[i].id = null;
                    this.delayCalls[i].params = null;
                    if (this.delayCalls[i].timer) {
                        this.delayCalls[i].timer.reset();
                        this.delayCalls[i].timer.removeCustomEventListener(TimerEvent.TIMER_COMPLETE, this.handleComplete);
                        this.delayCalls[i].timer = null;
                    }
                    this.delayCalls[i].params = null;
                    this.delayCalls[i] = null;
                    this.delayCalls.splice(i, 1);
                }
            }
        }
    }

    //----------------------------------
    //  Public-Static:
    //----------------------------------

    public static anyDelaysAreRunning(): boolean {
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].timer && this.delayCalls[i].timer.running) {
                    return true;
                }
            }
        }
        return false;
    }

    public static callLater(functionToCall: () => void, delayMS: number, id: string = null, ...args): void {
        if (this.delayCalls == null) {
            this.delayCalls = [];
        }
        this.checkForDuplicateIds(id);
        const curCall: DelayData = new DelayData();
        curCall.timer = new Timer(1000, 1);
        curCall.timer.addCustomEventListener(TimerEvent.TIMER_COMPLETE, this.handleComplete);
        curCall.callback = functionToCall;
        curCall.timer.delay = delayMS;
        if (id != null) {
            curCall.id = id;
        }
        curCall.params = args;
        this.delayCalls.push(curCall);
        curCall.timer.start();
    }

    public static cancelAllCalls(): void {
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].timer) {
                    this.delayCalls[i].timer.reset();
                    this.delayCalls[i].timer.removeCustomEventListener(TimerEvent.TIMER_COMPLETE, this.handleComplete);
                    this.delayCalls[i].timer = null;
                }
                this.delayCalls[i].callback = null;
                this.delayCalls[i].id = null;
                this.delayCalls[i].params = null;
                this.delayCalls[i] = null;
            }
            this.delayCalls = null;
        }
    }

    public static cancelCall(id: string): void {
        this.removeCall(id);
    }

    public static delayIsRunning(id: string): boolean {
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].id && this.delayCalls[i].id == id) {
                    return true;
                }
            }
        }
        return false;
    }

    public static destroy(): void {
        this.cancelAllCalls();
    }

    public static getAllDelaysRunning(): DelayData[] {
        const runningDelays: DelayData[] = [];
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].timer && this.delayCalls[i].timer.running) {
                    runningDelays.push(this.delayCalls[i]);
                }
            }
        }
        return runningDelays;
    }

    public static pauseCall(id: string): void {
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].id == id) {
                    if (this.delayCalls[i].timer) {
                        this.delayCalls[i].timer.stop();
                    }
                }
            }
        }
    }

    public static resumeCall(id: string): void {
        if (this.delayCalls) {
            const len: number = this.delayCalls.length;
            for (let i: number = 0; i < len; i++) {
                if (this.delayCalls[i].id == id) {
                    if (this.delayCalls[i].timer) {
                        this.delayCalls[i].timer.start();
                    }
                }
            }
        }
    }
}
